import { getInstance } from "../instance";
import {
  MemberSnapshot,
  PASSWORD_REVEAL_REFUSAL_STATUS,
  PASSWORD_REVEAL_RETIRED,
  PasswordRevealLimitScope,
  PasswordRevealRefusal,
  PasswordRevealRefusalCode,
  PasswordRevealRetiredError,
  RevealMemberPasswordOptions,
  RevealMemberPasswordResponse,
} from "./types";

/**
 * DS-native member snapshot for the ABO member-password screen (HubSpot
 * decommission §3.2). The endpoint is versioned (`/v1/abo/...`), so — unlike the
 * un-versioned `/auth/roles` calls — we use a RELATIVE path on the shared
 * `dsInstance` whose baseURL already carries `/v1` (same pattern as
 * `api/hubspot.ts`'s `/abo/update-hubspot-password`).
 */
const instance = getInstance();

export const getMemberSnapshot = async (
  email: string
): Promise<MemberSnapshot> => {
  const url = `/abo/member-account/${encodeURIComponent(email)}`;
  return instance
    .get<MemberSnapshot>(url)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

/** The header that carries the operator's FusionAuth token (DS task 115). */
export const OPERATOR_TOKEN_HEADER = "x-afy-operator-token";

/**
 * Reveal a member's password from the DS vault (DS task 111, ADR-015). DS
 * checks that the operator is a DS admin, writes an audit event (with the
 * optional reason) before answering, and rate-limits the reveal. The operator
 * is named by `x-afy-user-email` and proven by the FusionAuth token in
 * `x-afy-operator-token` (DS task 115; when DS requires it, the token's e-mail
 * wins). The token goes on this request only, as a per-request header, never
 * on the shared instance. Called on an explicit operator action only; the
 * caller must keep the result out of any cache or persistent storage.
 * Refusals are typed (see `getPasswordRevealRefusal`). The optional
 * `surface` / `intent` are audit provenance (ADR-002), sent in the body only
 * when set; an older DS ignores them.
 */
export const revealMemberPassword = async (
  email: string,
  {
    operatorEmail,
    operatorToken,
    reason,
    surface,
    intent,
  }: RevealMemberPasswordOptions
): Promise<RevealMemberPasswordResponse> => {
  const trimmedReason = reason?.trim();
  const token = operatorToken?.trim();
  return instance
    .post<RevealMemberPasswordResponse>(
      "/abo/member-account/reveal-password",
      {
        email,
        ...(trimmedReason ? { reason: trimmedReason } : {}),
        ...(surface ? { surface } : {}),
        ...(intent ? { intent } : {}),
      },
      {
        headers: {
          "x-afy-user-email": operatorEmail,
          ...(token ? { [OPERATOR_TOKEN_HEADER]: token } : {}),
        },
      }
    )
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

const isRefusalCode = (code: unknown): code is PasswordRevealRefusalCode =>
  typeof code === "string" &&
  Object.prototype.hasOwnProperty.call(PASSWORD_REVEAL_REFUSAL_STATUS, code);

const isLimitScope = (scope: unknown): scope is PasswordRevealLimitScope =>
  scope === "operator" || scope === "target";

/**
 * The typed refusal behind a failed reveal, or `null` for any other failure.
 * A refusal is recognised only when the body's `code` is a known reveal code
 * AND the status is the one DS answers that code with (ADR-011); the rate
 * limit also carries its `scope`. Structural on purpose: it reads the axios
 * error shape without depending on `axios.isAxiosError`.
 */
export const getPasswordRevealRefusal = (
  error: unknown
): PasswordRevealRefusal | null => {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const response = (error as { response?: unknown }).response;
  if (typeof response !== "object" || response === null) {
    return null;
  }
  const { status, data } = response as { status?: unknown; data?: unknown };
  if (typeof data !== "object" || data === null) {
    return null;
  }
  const { code, scope } = data as { code?: unknown; scope?: unknown };
  if (!isRefusalCode(code) || PASSWORD_REVEAL_REFUSAL_STATUS[code] !== status) {
    return null;
  }
  return isLimitScope(scope) ? { code, scope } : { code };
};

/**
 * True when a reveal failed because an older DS retired it (410 with
 * `code: 'PASSWORD_REVEAL_RETIRED'`, DS task 50).
 */
export const isPasswordRevealRetiredError = (
  error: unknown
): error is PasswordRevealRetiredError =>
  getPasswordRevealRefusal(error)?.code === PASSWORD_REVEAL_RETIRED;
